import type { ReactNode } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import type { ProviderLicenseType } from '@store/generated/organizations';
import {
  PROVIDER_LICENSE_TYPE_OPTIONS,
  type ProviderLicenseTypeOption,
} from '@utils/providerLicenseTypes';

import type {
  GeneratedIntakeFormRequest,
  GeneratedIntakeGroupHelp,
  IntakeFormConfiguration,
  IntakeHubProviderRosterEntry,
} from '../../types';

import { GroupBlurb, GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { IntakePayerTable, type IntakePayerTableProps } from './IntakePayerTable';
import { cardSx, chipSx, groupTitleSx, wf } from './tokens';
import type { PayerRow } from './types';
import { useGroupHelpPanel } from './useGroupHelpPanel';

type ProviderRosterProgress = NonNullable<IntakeHubProviderRosterEntry['progress']>;

export interface EligibleProvider {
  userId: string;
  name: string;
  percentComplete?: ProviderRosterProgress['percent_complete'];
  status?: NonNullable<IntakeHubProviderRosterEntry['status']>;
  /** ISO timestamp when an admin verified this provider's intake, if any. */
  verifiedAt?: string | null;
}

export interface IntakeConfig {
  // Unanswered fields exist only in the local draft; API writes require complete answers.
  role?: GeneratedIntakeFormRequest['role'];
  // Use the response's product list; local configuration always supplies an array.
  products: NonNullable<IntakeFormConfiguration['products']>;
  isPrescriber?: GeneratedIntakeFormRequest['is_prescriber'];
  licenseTypes: ProviderLicenseType[];
  practiceType?: GeneratedIntakeFormRequest['practice_type'];
  completedPages: NonNullable<IntakeFormConfiguration['completed_pages']>;
}

export function hasRequiredIntakeAnswers(
  config: IntakeConfig,
): config is IntakeConfig & Required<Pick<IntakeConfig, 'role' | 'isPrescriber' | 'practiceType'>> {
  return (
    Boolean(config.role) &&
    typeof config.isPrescriber === 'boolean' &&
    Boolean(config.practiceType) &&
    config.licenseTypes.length > 0
  );
}

interface IntakeConfigPageProps {
  serviceSelection?: React.ReactNode;
  config: IntakeConfig;
  onChange: (next: Partial<IntakeConfig>) => void;
  payerRows: PayerRow[];
  stediConnection?: ReactNode;
  payerTableProps?: Omit<IntakePayerTableProps, 'derivedSentence' | 'rows' | 'blurb'>;
  eligibleProviders?: EligibleProvider[];
  currentProviderName?: string;
  eligibleProvidersLoading?: boolean;
  eligibleProvidersError?: boolean;
  rosterCandidates?: EligibleProvider[];
  canManageRoster?: boolean;
  onAddRosterProvider?: (userId: string) => void;
  onRemoveRosterProvider?: (userId: string) => void;
  isAddingRosterProvider?: boolean;
  derivedSentence: string | null;
  onContinue: () => void;
  groups?: GeneratedIntakeGroupHelp[];
  onHelpAction?: (action: 'message_specialist') => void;
  organizationScope?: boolean;
  readOnlyReason?: string;
  continueLabel?: string | null;
  continueBlockedReason?: string;
  saveLabel?: string;
  onSave?: () => void;
  saveDisabled?: boolean;
  saveErrorMessage?: string;
  /** Recovers from whatever produced `saveErrorMessage`; renders an action in the banner. */
  onSaveErrorAction?: () => void;
  /** Label for that action, e.g. 'Retry' or 'Reload list'. */
  saveErrorActionLabel?: string;
  saveMode?: 'autosave' | 'explicit';
  isSaving?: boolean;
  saveErrorCount?: number;
}

const ROLES: { value: IntakeConfig['role']; label: string }[] = [
  { value: 'manager_who_also_practices', label: 'Clinic manager + provider' },
  { value: 'clinic_manager', label: 'Clinic manager' },
  { value: 'provider', label: 'Provider' },
];

const PRODUCT_LABELS: Record<IntakeConfig['products'][number], string> = {
  jotbill: 'JotBill (billing)',
  jotcred: 'JotCred (credentialing)',
};

function providerIntakeStatusLabel(status: EligibleProvider['status']): string {
  if (status === 'complete') return 'Complete';
  if (status === 'in_progress') return 'In progress';
  if (status === 'invited') return 'Invited';
  return 'Unavailable';
}

function Radio({
  selected,
  label,
  onClick,
  disabled = false,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={event => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '15px',
        padding: '4px 0',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      <Box
        component="span"
        sx={{
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          border: `1.5px solid ${selected ? wf.blue : wf.faint}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {selected && (
          <Box
            component="span"
            sx={{ width: '8px', height: '8px', borderRadius: '50%', background: wf.blue }}
          />
        )}
      </Box>
      {label}
    </Box>
  );
}

function GroupCard({
  title,
  blurb,
  chip,
  help,
  children,
  full,
}: {
  title: string;
  blurb?: string | null;
  chip?: string;
  help?: React.ReactNode;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <Box sx={{ ...cardSx, ...(full ? { gridColumn: '1 / -1' } : {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <Box sx={{ ...groupTitleSx, fontSize: '16px' }}>{title}</Box>
        {help}
        {chip && <Box sx={chipSx('mut')}>{chip}</Box>}
      </Box>
      <GroupBlurb
        blurb={blurb}
        sx={{ mt: '-6px', mb: '10px', fontSize: '14px', lineHeight: 1.5 }}
      />
      {children}
    </Box>
  );
}

export function IntakeConfigPage({
  serviceSelection,
  config,
  onChange,
  payerRows,
  stediConnection,
  payerTableProps,
  eligibleProviders = [],
  currentProviderName,
  eligibleProvidersLoading = false,
  eligibleProvidersError = false,
  rosterCandidates = [],
  canManageRoster = false,
  onAddRosterProvider,
  onRemoveRosterProvider,
  isAddingRosterProvider = false,
  derivedSentence,
  onContinue,
  groups = [],
  onHelpAction = () => undefined,
  organizationScope = false,
  readOnlyReason,
  continueLabel = "See what we'll need →",
  continueBlockedReason,
  saveLabel,
  onSave,
  saveDisabled,
  saveErrorMessage,
  onSaveErrorAction,
  saveErrorActionLabel = 'Retry',
  saveMode = 'autosave',
  isSaving = false,
  saveErrorCount = 0,
}: IntakeConfigPageProps) {
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } = useGroupHelpPanel(groups);
  const drawerId = 'intake-config-group-help';
  const blurbFor = (groupId: string) =>
    groups.find(entry => entry.group_id === groupId)?.blurb ?? null;
  const helpFor = (groupId: string, fallbackName: string) => {
    const group = groups.find(entry => entry.group_id === groupId);
    if (!group?.help_panel) return null;
    return (
      <GroupHelpTrigger
        groupName={group.group_name || fallbackName}
        controlsId={drawerId}
        expanded={selectedGroupId === groupId}
        onOpen={opener => openGroup(groupId, opener)}
      />
    );
  };
  const showsProviderRosterHandoff =
    organizationScope || (config.practiceType === 'group' && config.role !== 'provider');
  const showsPracticeBillingSetup =
    organizationScope ||
    config.role === 'clinic_manager' ||
    config.role === 'manager_who_also_practices';

  return (
    <>
      <Box sx={{ '& h1': { fontSize: '20px' }, '& p': { fontSize: '15px', lineHeight: 1.5 } }}>
        <IntakePageHeader
          title={organizationScope ? 'Organization intake settings' : 'A few questions first'}
          sub={
            organizationScope
              ? 'Review the intake provider roster and manage the organization payer list. Select a provider for provider-specific intake.'
              : 'Your answers decide exactly what we ask for — nothing else is shown.'
          }
        />
      </Box>

      {readOnlyReason && <Alert severity="info">{readOnlyReason}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: '12px',
          '& .MuiInputBase-root': { fontSize: '15px' },
          '& .MuiInputLabel-root': { fontSize: '15px' },
        }}
      >
        {serviceSelection && <Box sx={{ ...cardSx, gridColumn: '1 / -1' }}>{serviceSelection}</Box>}
        {!organizationScope && (
          <>
            <GroupCard title="Your role" blurb={blurbFor('C01')} help={helpFor('C01', 'Your role')}>
              {ROLES.map(role => (
                <Radio
                  key={role.value}
                  selected={config.role === role.value}
                  label={role.label}
                  onClick={() => onChange({ role: role.value })}
                  disabled={Boolean(readOnlyReason)}
                />
              ))}
            </GroupCard>

            {!serviceSelection && (
              <GroupCard
                title="What we're setting up"
                chip="practice services"
                blurb={blurbFor('C02')}
                help={helpFor('C02', "What we're setting up")}
              >
                {config.products.length > 0 ? (
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {config.products.map(product => (
                      <Box
                        component="li"
                        key={product}
                        sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '4px' }}
                      >
                        <Box
                          component="span"
                          aria-hidden="true"
                          sx={{ color: wf.green, fontSize: '14px', lineHeight: 1 }}
                        >
                          ✓
                        </Box>
                        <Typography sx={{ fontSize: '15px' }}>{PRODUCT_LABELS[product]}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: '15px', color: wf.muted }}>
                    No services have been assigned to this intake yet. Contact your JotPsych team.
                  </Typography>
                )}
              </GroupCard>
            )}

            <GroupCard
              title="Do you prescribe medication?"
              blurb={blurbFor('C03')}
              help={helpFor('C03', 'Do you prescribe medication?')}
            >
              <Radio
                selected={config.isPrescriber === true}
                label="Yes"
                onClick={() => onChange({ isPrescriber: true })}
                disabled={Boolean(readOnlyReason)}
              />
              <Radio
                selected={config.isPrescriber === false}
                label="No"
                onClick={() => onChange({ isPrescriber: false })}
                disabled={Boolean(readOnlyReason)}
              />
            </GroupCard>

            <GroupCard
              title="Your license / credential type(s)"
              blurb={blurbFor('C04')}
              help={helpFor('C04', 'Your license / credential type(s)')}
            >
              <Autocomplete<ProviderLicenseTypeOption, true>
                disabled={Boolean(readOnlyReason)}
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                options={PROVIDER_LICENSE_TYPE_OPTIONS}
                value={PROVIDER_LICENSE_TYPE_OPTIONS.filter(option =>
                  config.licenseTypes.includes(option.value),
                )}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_event, options) =>
                  onChange({ licenseTypes: options.map(option => option.value) })
                }
                renderValue={(selected, getItemProps) =>
                  selected.map((option, index) => {
                    const { key, ...chipProps } = getItemProps({ index });
                    return <Chip key={key} label={option.value} size="small" {...chipProps} />;
                  })
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Select credentials"
                    placeholder={
                      config.licenseTypes.length === 0 ? 'Search credentials' : undefined
                    }
                    size="small"
                  />
                )}
              />
            </GroupCard>

            <GroupCard
              title="Solo or group practice"
              blurb={blurbFor('C05')}
              help={helpFor('C05', 'Solo or group practice')}
            >
              <Radio
                selected={config.practiceType === 'solo'}
                label="Solo"
                onClick={() => onChange({ practiceType: 'solo' })}
                disabled={Boolean(readOnlyReason)}
              />
              <Radio
                selected={config.practiceType === 'group'}
                label="Group"
                onClick={() => onChange({ practiceType: 'group' })}
                disabled={Boolean(readOnlyReason)}
              />
            </GroupCard>
          </>
        )}

        {showsProviderRosterHandoff && (
          <GroupCard title="Eligible providers" help={helpFor('R01', 'Eligible providers')}>
            {currentProviderName && (
              <Box
                aria-label="Current provider"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
              >
                <Typography sx={{ color: wf.ink, fontSize: '15px', fontWeight: 650 }}>
                  {currentProviderName}
                </Typography>
                {currentProviderName !== 'You' && <Chip label="You" size="small" />}
              </Box>
            )}
            {eligibleProvidersLoading ? (
              <Typography role="status" sx={{ fontSize: '15px', color: wf.muted, margin: 0 }}>
                Loading providers…
              </Typography>
            ) : eligibleProvidersError ? (
              <Typography role="alert" sx={{ fontSize: '15px', color: wf.muted, margin: 0 }}>
                We couldn&apos;t load this practice&apos;s providers. Refresh and try again.
              </Typography>
            ) : (
              <>
                {eligibleProviders.length === 0 ? (
                  <Typography sx={{ fontSize: '15px', color: wf.muted, margin: 0 }}>
                    {currentProviderName ? 'No additional providers.' : 'No providers added yet.'}
                  </Typography>
                ) : (
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {eligibleProviders.map(provider => {
                      const hasProgress =
                        provider.percentComplete !== undefined && provider.status !== undefined;

                      return (
                        <Box
                          component="li"
                          key={provider.userId}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              sm: 'minmax(140px, 1fr) minmax(130px, 1fr) auto auto',
                            },
                            alignItems: 'center',
                            gap: { xs: '6px', sm: '12px' },
                            py: '8px',
                            borderTop: `1px solid ${wf.lineSoft}`,
                          }}
                        >
                          <Typography sx={{ color: wf.ink, fontSize: '15px', fontWeight: 650 }}>
                            {provider.name}
                          </Typography>
                          <Box>
                            {hasProgress ? (
                              <>
                                <LinearProgress
                                  aria-label={`${provider.name} intake progress`}
                                  variant="determinate"
                                  value={provider.percentComplete}
                                  sx={{
                                    height: 6,
                                    borderRadius: 4,
                                    backgroundColor: wf.track,
                                    '& .MuiLinearProgress-bar': { backgroundColor: wf.blue },
                                  }}
                                />
                                <Typography sx={{ color: wf.muted, fontSize: '14px', mt: '2px' }}>
                                  {provider.percentComplete}%
                                </Typography>
                              </>
                            ) : (
                              <Typography sx={{ color: wf.muted, fontSize: '14px' }}>
                                Progress unavailable
                              </Typography>
                            )}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              justifySelf: { xs: 'start', sm: 'end' },
                              borderRadius: '12px',
                              border: `1px solid ${provider.status === 'complete' ? 'transparent' : wf.line}`,
                              backgroundColor:
                                provider.status === 'complete' ? wf.greenSoft : wf.surfaceAlt,
                              color: provider.status === 'complete' ? wf.green : wf.muted,
                              fontSize: '13px',
                              fontWeight: 650,
                              px: '10px',
                              py: '3px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {providerIntakeStatusLabel(provider.status)}
                          </Box>
                          {canManageRoster && onRemoveRosterProvider && !readOnlyReason && (
                            <Button
                              aria-label={`Remove ${provider.name}`}
                              size="small"
                              disabled={isAddingRosterProvider}
                              onClick={() => onRemoveRosterProvider(provider.userId)}
                              sx={{ fontSize: '14px', minWidth: 0, color: wf.muted }}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {canManageRoster && onAddRosterProvider && !readOnlyReason && (
                  <Autocomplete
                    options={rosterCandidates}
                    getOptionLabel={option => option.name}
                    value={null}
                    blurOnSelect
                    disabled={isAddingRosterProvider}
                    loading={isAddingRosterProvider}
                    loadingText="Adding provider…"
                    onChange={(_event, option) => {
                      if (option) onAddRosterProvider(option.userId);
                    }}
                    noOptionsText="No more practice members to add"
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder={
                          isAddingRosterProvider ? 'Adding provider…' : 'Add a provider…'
                        }
                        slotProps={{
                          ...params.slotProps,
                          htmlInput: {
                            ...params.slotProps.htmlInput,
                            'aria-label': 'Add a provider to the intake roster',
                          },
                          input: {
                            ...params.slotProps.input,
                            endAdornment: (
                              <>
                                {isAddingRosterProvider && <CircularProgress size={16} />}
                                {params.slotProps.input.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                    sx={{ mt: '8px', maxWidth: '320px' }}
                  />
                )}
              </>
            )}
          </GroupCard>
        )}

        {showsPracticeBillingSetup && (
          <>
            <IntakePayerTable
              {...payerTableProps}
              rows={payerRows}
              derivedSentence={derivedSentence}
              blurb={blurbFor('C07')}
              help={helpFor('C07', 'Your payer list')}
            />
            {stediConnection}
          </>
        )}
      </Box>

      {saveErrorCount > 0 && (
        <Box sx={{ color: 'error.main', fontSize: '0.8rem' }}>
          {saveErrorCount} change{saveErrorCount === 1 ? '' : 's'} could not be saved.
        </Box>
      )}

      {saveErrorMessage && (
        <Alert
          action={
            onSaveErrorAction && (
              <Button color="inherit" onClick={onSaveErrorAction} size="small">
                {saveErrorActionLabel}
              </Button>
            )
          }
          severity="error"
        >
          {saveErrorMessage}
        </Alert>
      )}

      <IntakePageFooter
        primaryLabel={continueLabel ?? undefined}
        onPrimary={onContinue}
        disabled={
          config.products.length === 0 ||
          isSaving ||
          Boolean(continueBlockedReason) ||
          (!organizationScope && !hasRequiredIntakeAnswers(config))
        }
        disabledReason={
          continueBlockedReason ??
          (!organizationScope && !hasRequiredIntakeAnswers(config)
            ? 'Answer each question and select at least one credential to continue.'
            : undefined)
        }
        saveLabel={saveLabel}
        onSave={onSave}
        saveDisabled={
          saveDisabled ||
          (!organizationScope && !hasRequiredIntakeAnswers(config)) ||
          (saveMode === 'explicit' && isSaving)
        }
      />
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
    </>
  );
}
